import { create } from "zustand";
import type {
  DemoCallInfoResponse,
  DemoCallPhase,
} from "~/features/demoCall/types";
import { trackDemoCallBannerStep } from "~/lib/demoCallBannerTracking";
import { EVENT_NAMES, trackEvent } from "~/lib/tracking";

/**
 * 着信バナーを自動的に閉じる（不在着信扱い）までの時間。
 * 実際の着信同様、一定時間応答が無ければ鳴り止んでバナーを消す。
 * 着信バナーのカウントダウン表示と DemoCallOverlay の自動閉じで共用する。
 */
export const DEMO_INCOMING_TIMEOUT_MS = 60_000;

type DemoCallState = {
  phase: DemoCallPhase;
  info: DemoCallInfoResponse | null;
  /** 着信開始時刻(ms)。カウントダウン表示・自動閉じの基点。着信中以外は null */
  incomingStartedAt: number | null;
  /** 通話が終わった理由。終了画面の文言に使う */
  endReason: DemoCallEndReason;
};

/** normal = 自分で終了 / pointShortage = 架空のポイントが尽きた */
export type DemoCallEndReason = "normal" | "pointShortage";

type DemoCallActions = {
  showIncoming: (info: DemoCallInfoResponse) => void;
  answer: () => void;
  decline: () => void;
  endCall: (reason?: DemoCallEndReason) => void;
  reset: () => void;
};

// 「表示済みか」は userId 単位で ~/utils/demoCallEligibility が管理するため、
// このストア自体は永続化不要（phase/info はセッション内のみの一時状態）。
export const useDemoCallStore = create<DemoCallState & DemoCallActions>(
  (set, get) => ({
    phase: "idle",
    info: null,
    incomingStartedAt: null,
    endReason: "normal",
    showIncoming: (info) =>
      set({ phase: "incomingRinging", info, incomingStartedAt: Date.now() }),
    answer: () => set({ phase: "inCall" }),
    decline: () => {
      // 着信ボタン・DemoCallOverlay の自動閉じ等、複数箇所から呼ばれるため
      // 実際に着信中だった場合のみ計測する（重複/無関係な呼び出しでの誤送信防止）。
      const wasRinging = get().phase === "incomingRinging";
      set({ phase: "idle", info: null, incomingStartedAt: null });
      if (wasRinging) {
        trackEvent(EVENT_NAMES.DECLINE_DEMO_CALL);
        trackDemoCallBannerStep("decline");
      }
    },
    endCall: (reason = "normal") => set({ phase: "ended", endReason: reason }),
    reset: () => set({ phase: "idle", info: null, incomingStartedAt: null }),
  }),
);
